// @owners { team: patients-team }
import { type CartItem } from '@alto/orders_api/types/v1/cart_item';
import { transformCartPriceType } from '~shared/features/cart/selectors/getReduxCart';
import { type PrescriptionHashWithUpdatedAt } from '~shared/features/prescriptions/selectors/getPrescriptionByID';
import {
  type ItemPriceTotal,
  getItemPricingTotal,
} from '~shared/features/pricing/selectors/getPricingInfoForPrescription';
import { type OrderPricing } from '~shared/features/pricing/types';
import { memoizeLatest } from '~shared/helpers/memoizeLatest';
import { type Prescription } from '~shared/types/clients';

type CartPrescriptionDetailsInput = {
  cartItems: CartItem[];
  prescriptionsHash: PrescriptionHashWithUpdatedAt;
  cartUpdatedAt: number;
};

type CartPricingTotalsInput = {
  cartItems: CartItem[];
  orderPricing: OrderPricing;
  cartUpdatedAt: number;
  orderPricingUpdatedAt: number;
};

/**
 * Syncs redux prescription data with react-query cart items (no pricing).
 */
export const getCartPrescriptionDetails = memoizeLatest(
  ({ cartItems, prescriptionsHash }: CartPrescriptionDetailsInput) => {
    const cartPrescriptionIds: number[] = [];
    const cartPrescriptionIdHash: Record<number, Prescription> = {};
    const cartCompounds: Prescription[] = [];
    const cartInjectableCompounds: CartItem[] = [];
    const cartInjectable: CartItem[] = [];
    const cartPrescriptions: Prescription[] = [];

    cartItems.forEach((cartItem) => {
      if (cartItem.resource_type !== 'Prescription') return;
      const prescription = prescriptionsHash.prescriptions[cartItem.resource_id];
      if (!prescription) return;
      cartPrescriptions.push(prescription);
      cartPrescriptionIds.push(prescription.id);
      cartPrescriptionIdHash[prescription.id] = prescription;
      if (prescription.injectable) {
        cartInjectable.push(cartItem);
      }
      if (prescription.is_compound) {
        cartCompounds.push(prescription);
      }
      if (prescription.injectable && prescription.is_compound) {
        cartInjectableCompounds.push(cartItem);
      }
    });

    return {
      cartPrescriptions,
      cartPrescriptionIds,
      cartCompounds,
      cartPrescriptionIdHash,
      cartInjectableCompounds,
      cartInjectable,
    };
  },
  ({ cartUpdatedAt, prescriptionsHash }) => `${cartUpdatedAt}-${prescriptionsHash.updatedAt}`,
);

/**
 * Per-prescription cart pricing totals from order pricing (requires useOrderPricing subscription).
 */
export const getCartPricingTotals = memoizeLatest(
  ({ cartItems, orderPricing }: CartPricingTotalsInput) => {
    const cartPricingTotals: Record<number, ItemPriceTotal> = {};

    cartItems.forEach((cartItem) => {
      if (cartItem.resource_type !== 'Prescription') return;
      const pricing = orderPricing.item_pricing[cartItem.resource_id];
      if (pricing) {
        const paymentTotal = getItemPricingTotal(pricing, transformCartPriceType(cartItem.selected_price_type));
        cartPricingTotals[cartItem.resource_id] = paymentTotal;
      }
    });

    return { cartPricingTotals };
  },
  ({ cartUpdatedAt, orderPricingUpdatedAt }) => `${cartUpdatedAt}-${orderPricingUpdatedAt}`,
);
